import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import { toast } from "react-toastify";

import { API_bulk_grant_store_access } from "../../../../constants";
import { postRightworkAPI } from "../../requests/utilities/requests";
import {
  SPECIAL_ACCESS_ICONS,
  SPECIAL_ACCESS_STYLES,
} from "../../requests/manager/employees/EmployeesTableRow";
import { PERMISSION_OPTIONS, STORE_OVERRIDE_OPTIONS } from "./constants";
import { summarizeFailureReasons, logBulkFailures } from "./bulkResults";


export default function BulkGrantStoreAccessModal({
  open,
  onClose,
  selectedUserIds,
  companyId,
  companyStores,
  onSuccess,
  initialStoreIds,
  excludeStoreIds,
  initialPermissionType,
  initialOverrides,
}) {
  // Drawer's "+ Add to store" path passes excludeStoreIds with the user's
  // already-active store memberships so the picker can't double-grant.
  const visibleStores = React.useMemo(() => {
    if (!excludeStoreIds || excludeStoreIds.length === 0) return companyStores;
    const exclude = new Set(excludeStoreIds);
    return companyStores.filter((s) => !exclude.has(s.id));
  }, [companyStores, excludeStoreIds]);
  const allStoresAlreadyGranted =
    excludeStoreIds && excludeStoreIds.length > 0 && visibleStores.length === 0;
  const [storeIds, setStoreIds] = React.useState([]);
  const [permissionType, setPermissionType] = React.useState("company_employee");
  const [overrides, setOverrides] = React.useState([]);
  const [sendWelcome, setSendWelcome] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // Reset form when modal opens fresh. The drawer's "Reactivate" path passes
  // initialStoreIds; "+ Add to store" additionally passes initialPermissionType
  // + initialOverrides so the new membership mirrors the user's existing
  // privileges by default. Toolbar opens with no prefill.
  React.useEffect(() => {
    if (open) {
      setStoreIds(initialStoreIds || []);
      setPermissionType(initialPermissionType || "company_employee");
      setOverrides(initialOverrides || []);
      setSendWelcome(false);
    }
  }, [open, initialStoreIds, initialPermissionType, initialOverrides]);

  const userCount = selectedUserIds?.size || 0;
  const storeCount = storeIds.length;
  const totalAssignments = userCount * storeCount;
  const canSubmit = userCount > 0 && storeCount > 0 && !submitting;

  const toggleStore = (id) => {
    setStoreIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleOverride = (key) => {
    setOverrides((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const response = await postRightworkAPI(API_bulk_grant_store_access, {
        company_id: companyId,
        user_ids: Array.from(selectedUserIds).join(","),
        store_ids: storeIds.join(","),
        permission_type: permissionType,
        overrides: overrides.join(","),
        send_welcome: sendWelcome,
      });
      const payload = response?.data?.redux?.payload || {};
      const succeeded = payload.succeeded_count || 0;
      const failed = payload.failed_count || 0;
      if (failed === 0) {
        toast.success(`Granted access for ${succeeded} membership${succeeded === 1 ? "" : "s"}.`, { autoClose: 3000 });
      } else {
        const reasons = summarizeFailureReasons(payload.results);
        logBulkFailures("bulk_grant_store_access", payload.results);
        toast.warning(
          `Granted ${succeeded} of ${succeeded + failed}; ${failed} failed.${reasons ? ` Reasons: ${reasons}` : ""}`,
          { autoClose: 8000 },
        );
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Bulk grant failed.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={submitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Add to store</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          <p className="text-sm text-slate-600">
            Granting <span className="font-semibold">{userCount}</span> selected
            {userCount === 1 ? " user" : " users"} access to the selected stores.
            {totalAssignments > 0 && (
              <span className="block mt-1 text-xs text-slate-500">
                {totalAssignments} membership{totalAssignments === 1 ? "" : "s"} will be created or reactivated.
              </span>
            )}
          </p>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              Stores
            </p>
            <div className="max-h-[200px] overflow-y-auto rounded-lg border border-slate-200">
              {visibleStores.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-slate-500">
                  {allStoresAlreadyGranted
                    ? "Already a member of every store in this company."
                    : "No stores in this company."}
                </div>
              )}
              {visibleStores.map((s) => {
                const checked = storeIds.includes(s.id);
                const isClosed = s.status === "closed";
                return (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50"
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleStore(s.id)}
                      size="small"
                      sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, py: 0.5 }}
                    />
                    <StoreOutlinedIcon style={{ fontSize: 16 }} className="text-slate-400" />
                    <span className={`text-sm ${checked ? "text-violet-800 font-medium" : "text-slate-700"}`}>
                      {s.store_name}
                    </span>
                    {isClosed && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded bg-slate-100 text-slate-500">
                        closed
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              Permission
            </p>
            <Select
              value={permissionType}
              onChange={(e) => setPermissionType(e.target.value)}
              size="small"
              fullWidth
              sx={{ borderRadius: "8px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } }}
            >
              {PERMISSION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
              Special access (optional)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STORE_OVERRIDE_OPTIONS.map((opt) => {
                const checked = overrides.includes(opt.value);
                const Icon = SPECIAL_ACCESS_ICONS[opt.value];
                const styles = SPECIAL_ACCESS_STYLES[opt.value];
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleOverride(opt.value)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${
                      checked
                        ? "border-violet-600 bg-violet-50 text-violet-700"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {Icon && styles && (
                      <span
                        className={`inline-flex h-4 w-4 items-center justify-center rounded border ${styles.border} ${styles.bg}`}
                      >
                        <Icon className={styles.text} style={{ fontSize: 10 }} />
                      </span>
                    )}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={sendWelcome}
                onChange={(e) => setSendWelcome(e.target.checked)}
                sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" } }}
              />
            }
            label={<span className="text-sm text-slate-700">Send welcome email to newly created memberships</span>}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": { backgroundColor: "#5b21b6" },
              fontSize: 14,
              px: 2.5,
              py: 1,
            }}
          >
            {totalAssignments > 0 ? `Grant ${totalAssignments} access` : "Grant access"}
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
